import React from 'react';
import styled from 'styled-components';
import { PageContainer } from '@/components/layout';
import { Card, CardHeader, CardTitle } from '@/components/common';
import { useAuth } from '@/context';
import { useDashboard, useDailyStatusDigest, formatAmount } from '@/api/dashboard';
import { KpiCard } from '@/components/dashboard/KpiCard';
import { RecentList } from '@/components/dashboard/RecentList';
import { PendingPaymentsWidget } from '@/components/dashboard/PendingPaymentsWidget';
import { AttendanceConcernsWidget } from '@/components/dashboard/AttendanceConcernsWidget';
import { StaffLeaveWidget } from '@/components/dashboard/StaffLeaveWidget';
import { DailyStatusDigestCard } from '@/components/dashboard/DailyStatusDigestCard';
import { usePaymentDue } from '@/api/paymentDue';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// ── Styled components ─────────────────────────────────────────────────────────

const WelcomeBanner = styled.div`
  background: ${({ theme }) => theme.colors.cardBgGradient};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  position: relative;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3B82F6 0%, #60A5FA 50%, #93C5FD 100%);
  }
`;

const WelcomeTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.gold};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
`;

const WelcomeSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  margin: 0;
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  @media (max-width: 1200px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const RefreshNote = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textTertiary || theme.colors.textSecondary};
  text-align: right;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

// ── Icons ─────────────────────────────────────────────────────────────────────

const StudentIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const InvoiceIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
  </svg>
);
const PaymentIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

// ── Component ────────────────────────────────────────────────────────────────

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const { data, isLoading, isError, dataUpdatedAt } = useDashboard();
  const { data: pendingPayments, isLoading: pendingPaymentsLoading } = usePaymentDue('this_month');
  const { data: digest, isLoading: digestLoading } = useDailyStatusDigest(todayIso());

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : null;

  // Upcoming sessions list items
  const upcomingSessions = (data?.upcoming_sessions ?? []).map((s) => ({
    id: s.session_id,
    primary: s.subject_name,
    secondary: s.teacher_name ? `Teacher: ${s.teacher_name}` : undefined,
    right: new Date(s.starts_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    meta: new Date(s.starts_at).toLocaleDateString(),
  }));

  // Recent payments list items
  const recentPayments = (data?.recent_payments ?? []).map((p) => ({
    id: p.payment_id,
    primary: p.student_name ?? 'Unknown student',
    secondary: p.invoice_number ? `Invoice: ${p.invoice_number}` : undefined,
    right: formatAmount(p.amount),
    meta: new Date(p.paid_at).toLocaleDateString(),
  }));

  return (
    <PageContainer
      title="Admin Dashboard"
      subtitle="Live school overview"
      data-testid="candeza-dashboard-page"
    >
      <WelcomeBanner>
        <WelcomeTitle>
          Welcome back, {user?.email?.split('@')[0]}!
        </WelcomeTitle>
        <WelcomeSubtitle>
          Here&apos;s what&apos;s happening right now.
        </WelcomeSubtitle>
      </WelcomeBanner>

      {lastUpdated && (
        <RefreshNote data-testid="candeza-dashboard-last-updated">
          Last updated: {lastUpdated} · Auto-refreshes every 60s
        </RefreshNote>
      )}

      <KpiGrid data-testid="candeza-dashboard-kpi-grid">
        <KpiCard
          id="active-students"
          label="Active Students"
          value={data?.active_students ?? 0}
          icon={<StudentIcon />}
          color="#3B82F6"
          loading={isLoading}
          error={isError}
        />
        <KpiCard
          id="sessions-today"
          label="Sessions Today"
          value={data?.sessions_today ?? 0}
          icon={<CalendarIcon />}
          color="#22c55e"
          loading={isLoading}
          error={isError}
          subtitle={data ? `${data.sessions_this_week} this week` : undefined}
        />
        <KpiCard
          id="outstanding"
          label="Outstanding Invoices"
          value={isLoading ? '—' : formatAmount(data?.outstanding_invoices_total ?? 0)}
          icon={<InvoiceIcon />}
          color="#f59e0b"
          loading={isLoading}
          error={isError}
        />
        <KpiCard
          id="payments-month"
          label="Payments This Month"
          value={isLoading ? '—' : formatAmount(data?.payments_this_month_total ?? 0)}
          icon={<PaymentIcon />}
          color="#10b981"
          loading={isLoading}
          error={isError}
        />
      </KpiGrid>

      <ContentGrid>
        <Card padding="lg" data-testid="candeza-dashboard-upcoming-card">
          <CardHeader>
            <CardTitle>Upcoming Sessions</CardTitle>
          </CardHeader>
          <RecentList
            listId="upcoming-sessions"
            title=""
            items={upcomingSessions}
            loading={isLoading}
            error={isError}
            emptyMessage="No upcoming sessions scheduled"
          />
        </Card>

        <Card padding="lg" data-testid="candeza-dashboard-payments-card">
          <CardHeader>
            <CardTitle>Recent Payments</CardTitle>
          </CardHeader>
          <RecentList
            listId="recent-payments"
            title=""
            items={recentPayments}
            loading={isLoading}
            error={isError}
            emptyMessage="No payments recorded yet"
          />
        </Card>
      </ContentGrid>

      <ContentGrid style={{ marginTop: 24 }}>
        <PendingPaymentsWidget report={pendingPayments} isLoading={pendingPaymentsLoading} mode="full" />
        {digest && !digestLoading && <DailyStatusDigestCard data={digest} />}
      </ContentGrid>

      <ContentGrid style={{ marginTop: 24 }}>
        <AttendanceConcernsWidget />
        <StaffLeaveWidget />
      </ContentGrid>
    </PageContainer>
  );
};

export default AdminDashboard;
